import { DEFAULT_JIRA_ISSUE_TYPE_FILTER } from '../components/JiraIssueScopePicker';

export const JIRA_SCOPE = {
  PROJECT: 'project',
  ISSUE_FILTER: 'issue_filter',
  JQL: 'jql',
};

export const createDefaultJiraScope = () => ({
  type: JIRA_SCOPE.ISSUE_FILTER,
  jql_query: '',
  issue_type_filter: { ...DEFAULT_JIRA_ISSUE_TYPE_FILTER },
  preview_jql: '',
});

/** Apply a scope-mode change without letting the previous type override the new one. */
export const switchJiraScopeType = (prevScope, newType) => {
  const base = prevScope || {};
  if (newType === JIRA_SCOPE.ISSUE_FILTER) {
    return {
      ...createDefaultJiraScope(),
      ...base,
      type: JIRA_SCOPE.ISSUE_FILTER,
      issue_type_filter: base.issue_type_filter || { ...DEFAULT_JIRA_ISSUE_TYPE_FILTER },
    };
  }
  if (newType === JIRA_SCOPE.JQL) {
    return {
      ...base,
      type: JIRA_SCOPE.JQL,
      jql_query: String(base.jql_query || '').trim(),
    };
  }
  return {
    ...base,
    type: JIRA_SCOPE.PROJECT,
    jql_query: '',
  };
};

export const inferJiraScopeType = (scope = {}) => {
  if (scope?.type) return scope.type;
  if (scope?.issue_type_filter) return JIRA_SCOPE.ISSUE_FILTER;
  if (scope?.jql_query) return JIRA_SCOPE.JQL;
  return JIRA_SCOPE.PROJECT;
};

export const buildJiraScopePayload = (projectKey, scope) => {
  const row = scope || createDefaultJiraScope();
  const type = inferJiraScopeType(row);
  if (type === JIRA_SCOPE.JQL) {
    return {
      project_key: projectKey,
      jql_query: String(row.jql_query || '').trim(),
    };
  }
  if (type === JIRA_SCOPE.ISSUE_FILTER) {
    return {
      project_key: projectKey,
      issue_type_filter: row.issue_type_filter || { ...DEFAULT_JIRA_ISSUE_TYPE_FILTER },
    };
  }
  return {
    project_key: projectKey,
  };
};

export const jiraScopeReviewLabel = (scope) => {
  const type = inferJiraScopeType(scope);
  if (type === JIRA_SCOPE.JQL) return 'Custom JQL';
  if (type === JIRA_SCOPE.ISSUE_FILTER) return 'Issue types & statuses';
  return 'Entire project';
};

export const scopeRowFromStored = (scope = {}) => {
  const projectKey = String(scope?.project_key || '').trim();
  const type = inferJiraScopeType(scope);
  return {
    project_key: projectKey || null,
    jql_query: String(scope?.jql_query || '').trim() || null,
    type,
    issue_type_filter: scope?.issue_type_filter
      ? { ...DEFAULT_JIRA_ISSUE_TYPE_FILTER, ...scope.issue_type_filter }
      : { ...DEFAULT_JIRA_ISSUE_TYPE_FILTER },
    preview_jql: String(scope?.jql_query || '').trim(),
  };
};

export const jiraScopeFingerprint = (scope = {}) => {
  const row = scopeRowFromStored(scope);
  return JSON.stringify({
    project_key: row.project_key,
    type: row.type,
    jql_query: row.type === JIRA_SCOPE.JQL ? row.jql_query : '',
    issue_type_filter: row.type === JIRA_SCOPE.ISSUE_FILTER ? row.issue_type_filter : null,
  });
};

export const normalizeJiraScopesFromConfig = (jira = {}) => {
  const out = [];
  const seen = new Set();
  const rawScopes = Array.isArray(jira?.scopes) ? jira.scopes : [];
  rawScopes.forEach((scope) => {
    const row = scopeRowFromStored(scope);
    const projectKey = row.project_key;
    const jqlQuery = row.jql_query;
    if (!projectKey && !jqlQuery && row.type !== JIRA_SCOPE.ISSUE_FILTER) return;
    const key = projectKey || `__jql__:${jqlQuery}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(row);
  });
  if (out.length > 0) return out;
  const fallback = scopeRowFromStored({
    project_key: jira?.project_key,
    jql_query: jira?.jql_query,
  });
  if (!fallback.project_key && !fallback.jql_query) return [];
  return [fallback];
};

export const buildJiraScopesFormValue = (projectKeys, scopeByKey) => (
  (projectKeys || []).map((projectKey) => buildJiraScopePayload(projectKey, scopeByKey?.[projectKey]))
);

const readJiraConnectorMeta = (record = {}) => {
  const sourceMeta = record?.source_meta || {};
  const connector = sourceMeta.connector_document || {};
  const docMeta = connector.doc_metadata || connector.metadata || {};
  return { sourceMeta, connector, docMeta };
};

export const getJiraPipelineIssueType = (record) => {
  const { sourceMeta, connector, docMeta } = readJiraConnectorMeta(record);
  const value = sourceMeta.issuetype || docMeta.issuetype || docMeta.issue_type
    || connector.issuetype || connector.issue_type;
  return value ? String(value).trim() : null;
};

export const getJiraPipelineIssueStatus = (record) => {
  const { sourceMeta, connector, docMeta } = readJiraConnectorMeta(record);
  const value = sourceMeta.jira_status || sourceMeta.status || docMeta.status || connector.status;
  return value ? String(value).trim() : null;
};
